import React, { forwardRef } from 'react';
import { Stakeholder, Assignment } from '../../types/stakeholder';
import { Organization } from '../../types/organization';
import { MediaAsset } from '../../types/media';
import { WikiPage } from '../../types/wiki';
import { InfluenceScore, InfluenceCategory } from '../../services/influencesService';
import { ReportConfig } from './ReportConfigModal';
import { isHexColor } from '../ui';

interface ReportPreviewProps {
  entity: Stakeholder | Organization;
  entityType: 'stakeholder' | 'organization';
  config: ReportConfig;
  assignments?: Assignment[];
  media?: MediaAsset[];
  wikiPages?: WikiPage[];
  influenceScores?: InfluenceScore[];
  influenceCategories?: InfluenceCategory[];
  stakeholders?: Stakeholder[]; // For organization reports
}

/*
 * The report is a printable document: html2canvas captures it for the PDF and the print
 * window copies its markup. Keep it neutral, hairline-separated and self-contained. The only
 * colour is each influence category's own (dots and thin bars, inline styles so they survive
 * both paths); every score is also written as a number, so print stays legible without it.
 */

const PRINT_EXACT = { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties;

const CategoryDot: React.FC<{ color?: string }> = ({ color }) => (
  <span
    aria-hidden="true"
    className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
    style={{ ...PRINT_EXACT, backgroundColor: isHexColor(color) ? color : '#d4d4d4' }}
  />
);

const CategoryBar: React.FC<{ value: number; color?: string }> = ({ value, color }) => (
  <span
    aria-hidden="true"
    className="block h-1.5 w-full overflow-hidden rounded-full"
    style={{ ...PRINT_EXACT, backgroundColor: '#f5f5f5' }}
  >
    <span
      className="block h-full rounded-full"
      style={{
        ...PRINT_EXACT,
        width: `${Math.max(0, Math.min(100, value))}%`,
        backgroundColor: isHexColor(color) ? color : '#0a0a0a',
      }}
    />
  </span>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h2 className="mb-3 border-b border-neutral-200 pb-2 text-[16px] font-normal tracking-[-0.01em] text-ink">{title}</h2>
    {children}
  </section>
);

const Fields: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
);

const FieldItem: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label,
  className = '',
  children,
}) => (
  <div className={`min-w-0 ${className}`}>
    <h3 className="text-[12px] font-normal text-neutral-500">{label}</h3>
    <div className="text-[14px] text-ink [overflow-wrap:anywhere]">{children}</div>
  </div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[13px] text-neutral-400">{children}</p>
);

const TH: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="border-b border-neutral-200 px-3 py-2 first:pl-0 text-left text-[12px] font-normal text-neutral-500">{children}</th>
);

/** Cells wrap so a long ministry name fits a phone and the PDF page; `nowrap` keeps short values (dates) on one line */
const TD: React.FC<{ strong?: boolean; nowrap?: boolean; children: React.ReactNode }> = ({ strong = false, nowrap = false, children }) => (
  <td
    className={`px-3 py-2 align-top text-[13px] first:pl-0 ${nowrap ? 'whitespace-nowrap' : 'min-w-[7rem] [overflow-wrap:anywhere]'} ${strong ? 'text-ink' : 'text-neutral-600'}`}
  >
    {children}
  </td>
);

/** Phones: one row per entry with its details underneath, instead of a table too wide to read */
const StackedRows: React.FC<{ rows: Array<{ key: string; title: React.ReactNode; details: React.ReactNode[] }> }> = ({ rows }) => (
  <ul className="divide-y divide-neutral-200/70 sm:hidden">
    {rows.map((row) => (
      <li key={row.key} className="py-2.5 first:pt-0">
        <p className="text-[13px] text-ink [overflow-wrap:anywhere]">{row.title}</p>
        {row.details.filter(Boolean).map((detail, index) => (
          <p key={index} className="mt-0.5 text-[12px] text-neutral-500 [overflow-wrap:anywhere]">
            {detail}
          </p>
        ))}
      </li>
    ))}
  </ul>
);

const Score: React.FC<{ value: number }> = ({ value }) => (
  <span className="text-[22px] leading-none tracking-[-0.02em] tabular-nums text-ink">
    {value}
    <span className="text-[13px] text-neutral-400">/100</span>
  </span>
);

export const ReportPreview = forwardRef<HTMLDivElement, ReportPreviewProps>(({
  entity,
  entityType,
  config,
  assignments = [],
  media = [],
  wikiPages = [],
  influenceScores = [],
  influenceCategories = [],
  stakeholders = []
}, ref) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  const stripHtmlTags = (html: string): string => {
    if (!html) return '';

    // Create a temporary div element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove script and style elements completely
    const scripts = tempDiv.querySelectorAll('script, style');
    scripts.forEach(el => el.remove());

    // Get text content and clean it up
    let text = tempDiv.textContent || tempDiv.innerText || '';

    // Clean up extra whitespace and line breaks
    text = text
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/\n\s*\n/g, '\n\n') // Replace multiple line breaks with double line breaks
      .trim();

    return text;
  };

  const formatWikiContent = (content: string, maxLength: number = 1000): string => {
    const cleanText = stripHtmlTags(content);
    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength) + '...';
  };

  const isEnabled = (id: string) => !!config.sections.find(s => s.id === id)?.enabled;

  const categoryOf = (score: InfluenceScore) => {
    const category = influenceCategories.find(c => c.id === score.categoryId);
    return {
      label: score.category?.label || category?.label || score.categoryId,
      color: score.category?.color || category?.color,
    };
  };

  const renderHeader = (name: string, kind: string) => (
    <header className="border-b border-ink pb-5">
      <p className="text-[12px] text-neutral-500">{kind}</p>
      <h1 className="mt-1 text-[28px] font-normal leading-tight tracking-[-0.03em] text-ink">{name}</h1>
      <p className="mt-2 text-[12px] text-neutral-400">Generated {formatDate(new Date().toISOString())}</p>
    </header>
  );

  const renderMedia = () =>
    isEnabled('media-assets') && config.includeImages && (
      <Section title="Media & Documents">
        {media.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {media.map((asset) => (
              <div key={asset.id} className="rounded-xl border border-neutral-200 px-3.5 py-3">
                <h3 className="truncate text-[13px] font-normal text-ink">{asset.fileName}</h3>
                <p className="text-[12px] text-neutral-500">{asset.mimeType}</p>
                <p className="text-[12px] text-neutral-400">Uploaded {formatDate(asset.createdAt)}</p>
              </div>
            ))}
          </div>
        ) : (
          <Empty>No media files</Empty>
        )}
      </Section>
    );

  const renderWiki = () =>
    isEnabled('wiki-content') && (
      <Section title="Documentation">
        {wikiPages.length > 0 ? (
          <div className="space-y-3">
            {wikiPages.map((page) => (
              <div key={page.id} className="rounded-xl border border-neutral-200 px-4 py-3">
                <h3 className="mb-1.5 text-[14px] font-normal text-ink">{page.title}</h3>
                <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-neutral-600">
                  {formatWikiContent(page.content, 1500)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty>No documentation available</Empty>
        )}
      </Section>
    );

  const renderStakeholderReport = () => {
    const stakeholder = entity as Stakeholder;

    return (
      <div className="space-y-8">
        {renderHeader(`${stakeholder.firstName} ${stakeholder.lastName}`, 'Stakeholder report')}

        {/* Personal Information */}
        {isEnabled('personal-info') && (
          <Section title="Personal Information">
            <Fields>
              <FieldItem label="Full name">
                {stakeholder.firstName} {stakeholder.lastName}
              </FieldItem>
              <FieldItem label="Category">{stakeholder.category || 'Not specified'}</FieldItem>
              <FieldItem label="Location">{stakeholder.location || 'Not specified'}</FieldItem>
              <FieldItem label="Influence score">
                <span className="tabular-nums">{stakeholder.influenceScore || 0}/100</span>
              </FieldItem>
            </Fields>
            {stakeholder.bio && (
              <div className="mt-4 border-t border-neutral-200/70 pt-4">
                <h3 className="mb-1 text-[12px] font-normal text-neutral-500">Biography</h3>
                <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-ink">{stakeholder.bio}</p>
              </div>
            )}
          </Section>
        )}

        {/* Contact Details */}
        {isEnabled('contact-details') && (
          <Section title="Contact Information">
            <div>
              <Fields>
                <FieldItem label="Professional email">{stakeholder.professionalEmail || 'Not provided'}</FieldItem>
                <FieldItem label="Personal email">{stakeholder.personalEmail || 'Not provided'}</FieldItem>
                <FieldItem label="Professional phone">{stakeholder.professionalPhone || 'Not provided'}</FieldItem>
                <FieldItem label="Personal phone">{stakeholder.personalPhone || 'Not provided'}</FieldItem>
              </Fields>
            </div>
          </Section>
        )}

        {/* Social Media */}
        {isEnabled('social-media') && (
          <Section title="Social Media & Links">
            <div>
              <Fields>
                {stakeholder.linkedinUrl && (
                  <FieldItem label="LinkedIn">
                    <span className="break-all">{stakeholder.linkedinUrl}</span>
                  </FieldItem>
                )}
                {stakeholder.twitterUrl && (
                  <FieldItem label="Twitter/X">
                    <span className="break-all">{stakeholder.twitterUrl}</span>
                  </FieldItem>
                )}
                {stakeholder.facebookUrl && (
                  <FieldItem label="Facebook">
                    <span className="break-all">{stakeholder.facebookUrl}</span>
                  </FieldItem>
                )}
                {stakeholder.telegramUrl && (
                  <FieldItem label="Telegram">
                    <span className="break-all">{stakeholder.telegramUrl}</span>
                  </FieldItem>
                )}
                {stakeholder.whatsapp && <FieldItem label="WhatsApp">{stakeholder.whatsapp}</FieldItem>}
              </Fields>
            </div>
          </Section>
        )}

        {/* Assignments */}
        {isEnabled('assignments') && (
          <Section title="Current Assignments">
            {assignments.length > 0 ? (
              <>
              <StackedRows
                rows={assignments.map((assignment) => ({
                  key: assignment.id,
                  title: assignment.organizationName || assignment.organization?.name || 'Unknown',
                  details: [
                    [assignment.role, assignment.department].filter(Boolean).join(' · ') || 'Role not specified',
                    `${formatDate(assignment.startDate)} – ${assignment.endDate ? formatDate(assignment.endDate) : 'Present'}`,
                  ],
                }))}
              />
              <div className="hidden overflow-x-auto sm:block">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <TH>Organization</TH>
                      <TH>Role</TH>
                      <TH>Department</TH>
                      <TH>Period</TH>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200/70">
                    {assignments.map((assignment) => (
                      <tr key={assignment.id}>
                        <TD strong>{assignment.organizationName || assignment.organization?.name || 'Unknown'}</TD>
                        <TD>{assignment.role || 'Not specified'}</TD>
                        <TD>{assignment.department || 'Not specified'}</TD>
                        <TD nowrap>
                          {formatDate(assignment.startDate)} – {assignment.endDate ? formatDate(assignment.endDate) : 'Present'}
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <Empty>No current assignments</Empty>
            )}
          </Section>
        )}

        {/* Influence Data */}
        {isEnabled('influence-data') && (
          <Section title="Influence Analysis">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="mb-2 text-[12px] font-normal text-neutral-500">Influence score</h3>
                <Score value={stakeholder.influenceScore || 0} />
              </div>
              {influenceScores.length > 0 && (
                <div>
                  <h3 className="mb-1 text-[12px] font-normal text-neutral-500">Category breakdown</h3>
                  <div className="divide-y divide-neutral-200/70">
                    {influenceScores.map((score, index) => {
                      const category = categoryOf(score);
                      return (
                        <div
                          key={index}
                          className="grid grid-cols-[minmax(0,1fr)_4rem_3.25rem] items-center gap-3 py-1.5 text-[13px]"
                        >
                          <span className="flex min-w-0 items-center gap-2 text-neutral-600">
                            <CategoryDot color={category.color} />
                            <span className="truncate">{category.label}</span>
                          </span>
                          <CategoryBar value={score.score} color={category.color} />
                          <span className="text-right tabular-nums text-ink">{score.score}/100</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Media Assets */}
        {renderMedia()}

        {/* Wiki Content */}
        {renderWiki()}
      </div>
    );
  };

  const renderOrganizationReport = () => {
    const organization = entity as Organization;

    return (
      <div className="space-y-8">
        {renderHeader(organization.name, 'Organization report')}

        {/* Organization Information */}
        {isEnabled('organization-info') && (
          <Section title="Organization Details">
            <Fields>
              <FieldItem label="Organization name">{organization.name}</FieldItem>
              <FieldItem label="Type">{organization.type}</FieldItem>
              <FieldItem label="Website">
                <span className="break-all">{organization.website || 'Not provided'}</span>
              </FieldItem>
              <FieldItem label="Founded">
                {organization.createdAt ? formatDate(organization.createdAt) : 'Not specified'}
              </FieldItem>
            </Fields>
            {organization.description && (
              <div className="mt-4 border-t border-neutral-200/70 pt-4">
                <h3 className="mb-1 text-[12px] font-normal text-neutral-500">Description</h3>
                <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-ink">{organization.description}</p>
              </div>
            )}
          </Section>
        )}

        {/* Contact Details */}
        {isEnabled('contact-details') && (
          <Section title="Contact Information">
            <div>
              <Fields>
                <FieldItem label="Email">{organization.email || 'Not provided'}</FieldItem>
                <FieldItem label="Phone">{organization.phone || 'Not provided'}</FieldItem>
                <FieldItem label="Address" className="col-span-2">
                  <span className="whitespace-pre-wrap">{organization.address || 'Not provided'}</span>
                </FieldItem>
              </Fields>
            </div>
          </Section>
        )}

        {/* Stakeholders */}
        {isEnabled('stakeholders') && (
          <Section title="Key Stakeholders">
            {stakeholders.length > 0 ? (
              <div>
                <StackedRows
                  rows={stakeholders.slice(0, 10).map((stakeholder) => {
                    const assignment = stakeholder.assignments?.find(a => a.organizationId === organization.id);
                    return {
                      key: stakeholder.id,
                      title: `${stakeholder.firstName} ${stakeholder.lastName}`,
                      details: [
                        [assignment?.role, assignment?.department].filter(Boolean).join(' · ') || 'Role not specified',
                        stakeholder.professionalEmail || stakeholder.personalEmail || '',
                      ],
                    };
                  })}
                />
                <div className="hidden overflow-x-auto sm:block">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <TH>Name</TH>
                      <TH>Role</TH>
                      <TH>Department</TH>
                      <TH>Contact</TH>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200/70">
                    {stakeholders.slice(0, 10).map((stakeholder) => {
                      const assignment = stakeholder.assignments?.find(a => a.organizationId === organization.id);
                      return (
                        <tr key={stakeholder.id}>
                          <TD strong>
                            {stakeholder.firstName} {stakeholder.lastName}
                          </TD>
                          <TD>{assignment?.role || 'Not specified'}</TD>
                          <TD>{assignment?.department || 'Not specified'}</TD>
                          <TD>{stakeholder.professionalEmail || stakeholder.personalEmail || 'Not provided'}</TD>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                {stakeholders.length > 10 && (
                  <p className="mt-2 text-[12px] text-neutral-400">
                    and {stakeholders.length - 10} more stakeholders
                  </p>
                )}
              </div>
            ) : (
              <Empty>No stakeholders found</Empty>
            )}
          </Section>
        )}

        {/* Hierarchy */}
        {isEnabled('hierarchy') && (
          <Section title="Organizational Structure">
            <div className="space-y-3">
              {organization.parent && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-neutral-500">Parent organization</span>
                  <span className="text-ink">{organization.parent.name}</span>
                </div>
              )}
              {organization.children && organization.children.length > 0 && (
                <div>
                  <h3 className="mb-1 text-[12px] font-normal text-neutral-500">Subsidiaries</h3>
                  <ul className="divide-y divide-neutral-200/70">
                    {organization.children.map((child) => (
                      <li key={child.id} className="flex justify-between py-1.5 text-[13px]">
                        <span className="text-ink">{child.name}</span>
                        <span className="text-neutral-500">{child.type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!organization.parent && (!organization.children || organization.children.length === 0) && (
                <Empty>No organizational structure information</Empty>
              )}
            </div>
          </Section>
        )}

        {/* Influence Data */}
        {isEnabled('influence-data') && (
          <Section title="Influence Analysis">
            {influenceScores.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {influenceScores.map((score, index) => {
                  const category = categoryOf(score);
                  return (
                    <div key={index} className="rounded-xl border border-neutral-200 px-3.5 py-3">
                      <h3 className="mb-2 flex items-center gap-2 text-[12px] font-normal text-neutral-500">
                        <CategoryDot color={category.color} />
                        <span className="truncate">{category.label}</span>
                      </h3>
                      <Score value={score.score} />
                      <span className="mt-2.5 block">
                        <CategoryBar value={score.score} color={category.color} />
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty>No influence data available</Empty>
            )}
          </Section>
        )}

        {/* Media Assets */}
        {renderMedia()}

        {/* Wiki Content */}
        {renderWiki()}
      </div>
    );
  };

  return (
    <div
      ref={ref}
      className="mx-auto max-w-5xl bg-white px-5 py-6 sm:p-10 print:max-w-none print:p-8"
      style={{
        fontFamily: 'Geist, "Plus Jakarta Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        lineHeight: '1.6',
        color: '#0a0a0a',
        fontSize: '14px'
      }}
    >
      {entityType === 'stakeholder' ? renderStakeholderReport() : renderOrganizationReport()}
    </div>
  );
});

ReportPreview.displayName = 'ReportPreview';
